// src/context/CartContext.js

import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  applySpecialPricingToItem,
  getBulkSavingsLabel,
  getCartEggBulkSavings,
  getCartOriginalSubtotal,
  getCartSpecialSavings,
  getCartSubtotal,
  isCornProduct,
  isEggProduct
} from '../utils/pricing';
import { isProductOutOfStock, isUnlimitedInventory } from '../utils/inventoryAvailability';

// Create cart context
export const CartContext = createContext();

// API endpoint for product validation
const API_ENDPOINT = 'https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-inventory-product-get';

// Create the cart provider component
export const CartProvider = ({ children }) => {
  // Initialize state for cart items
  const [cartItems, setCartItems] = useState([]);
  // State for loading
  const [isLoading, setIsLoading] = useState(false);
  // State for validation message
  const [validationMessage, setValidationMessage] = useState('');
  // Track if cart has been initialized from localStorage
  const [isCartLoaded, setIsCartLoaded] = useState(false);
  
  // Use ref to prevent initial localStorage save
  const isInitialMount = useRef(true);
  
  // Sample products for demonstration - in a real app, this would come from an API or database
  const [products] = useState([
    { id: 1, name: "Organic Apples", price: 4.99, image: "https://images.unsplash.com/photo-1570913149827-d2ac84ab3f9a?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80", category: "Fruit" },
    { id: 2, name: "Heirloom Tomatoes", price: 5.99, image: "https://images.unsplash.com/photo-1518977822534-7049a61ee0c2?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80", category: "Vegetable" },
    { id: 3, name: "Fresh Strawberries", price: 6.99, image: "https://images.unsplash.com/photo-1464965911861-746a04b4bca6?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80", category: "Fruit" },
    { id: 4, name: "Farm Fresh Eggs", price: 5.49, image: "https://images.unsplash.com/photo-1489171078254-c3365319fdd7?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80", category: "Dairy" },
    { id: 5, name: "Wildflower Honey", price: 8.99, image: "https://images.unsplash.com/photo-1587049352851-8d4e89133924?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80", category: "Pantry" },
    { id: 6, name: "Seasonal Berries", price: 7.49, image: "https://images.unsplash.com/photo-1583513702439-2e611c58e93d?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&h=200&q=80", category: "Fruit" }
  ]);

  // Load cart from localStorage on component mount
  useEffect(() => {
    const loadCart = () => {
      try {
        const savedCart = localStorage.getItem('bucksCart');
        console.log('Loading cart from localStorage:', savedCart);
        
        if (savedCart && savedCart !== '[]') {
          const parsedCart = JSON.parse(savedCart);
          console.log('Parsed cart:', parsedCart);
          
          if (parsedCart && Array.isArray(parsedCart) && parsedCart.length > 0) {
            const repricedCart = parsedCart.map((item) => applySpecialPricingToItem(item, item.quantity || 1));
            setCartItems(repricedCart);
            console.log('Cart loaded successfully with', repricedCart.length, 'items');
          }
        }
        
        // Mark cart as loaded
        setIsCartLoaded(true);
        // Allow localStorage saves after initial load
        setTimeout(() => {
          isInitialMount.current = false;
        }, 100);
        
      } catch (error) {
        console.error('Error loading cart:', error);
        setCartItems([]);
        setIsCartLoaded(true);
        isInitialMount.current = false;
      }
    };
    
    loadCart();
  }, []);

  // Save cart to localStorage whenever it changes (but not on initial mount)
  useEffect(() => {
    if (!isInitialMount.current && isCartLoaded) {
      console.log('Saving cart to localStorage:', cartItems);
      updateLocalStorage(cartItems);
    }
  }, [cartItems, isCartLoaded]);

  // Update localStorage
  const updateLocalStorage = (items) => {
    try {
      localStorage.setItem('bucksCart', JSON.stringify(items));
      console.log('Cart saved to localStorage successfully');
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  };

  // Validate cart items to remove products that have been deleted or are no longer available
  const validateCartItems = async (items) => {
    if (!items || items.length === 0) {
      return;
    }
    
    setIsLoading(true);
    const validatedItems = [];
    const invalidItemNames = [];
    
    try {
      // For each item, check if it's a sample product or a real product
      for (const item of items) {
        // Check if this is a sample product first (no need to call API)
        const sampleProduct = products.find(p => p.id === item.id);
        
        if (sampleProduct) {
          // It's a sample product, keep it and ensure it has correct data
          validatedItems.push(applySpecialPricingToItem({
            ...sampleProduct,
            originalPrice: sampleProduct.price,
            quantity: item.quantity || 1
          }, item.quantity || 1));
        } else {
          // It's a real product from the API, validate it
          try {
            const response = await axios.get(API_ENDPOINT, {
              params: {
                product_id: item.id,
                include_out_of_stock: true
              }
            });
            
            // If product exists and is available, keep it in cart
            const availableStock = Number(response.data?.stock);
            const exceedsFiniteStock = (
              response.data
              && Number.isFinite(availableStock)
              && !isUnlimitedInventory(response.data)
              && Number(item.quantity || 1) > availableStock
            );
            if (response.data && response.data.id && !isProductOutOfStock(response.data) && !exceedsFiniteStock) {
              // Update product info from API (price may have changed)
              validatedItems.push(applySpecialPricingToItem({
                ...item,
                name: response.data.name,
                price: response.data.price,
                originalPrice: response.data.price,
                status: response.data.status,
                stock: response.data.stock,
                unit: response.data.unit || item.unit,
                image: response.data.image || item.image
              }, item.quantity || 1));
            } else {
              // Product not found - add to invalid list
              invalidItemNames.push(item.name);
            }
          } catch (error) {
            // API error or product not found - add to invalid list
            console.warn(`API validation failed for product ${item.id}:`, error);
            invalidItemNames.push(item.name);
          }
        }
      }
      
      // Update cart with validated items only if there were changes
      if (validatedItems.length !== items.length || invalidItemNames.length > 0) {
        setCartItems(validatedItems);
      }
      
      // Set validation message if items were removed
      if (invalidItemNames.length > 0) {
        let message = '';
        if (invalidItemNames.length === 1) {
          message = `${invalidItemNames[0]} has been removed from your basket because it is no longer available.`;
        } else if (invalidItemNames.length === 2) {
          message = `${invalidItemNames[0]} and ${invalidItemNames[1]} have been removed from your basket because they are no longer available.`;
        } else {
          const lastItem = invalidItemNames.pop();
          message = `${invalidItemNames.join(', ')} and ${lastItem} have been removed from your basket because they are no longer available.`;
        }
        setValidationMessage(message);
        
        // Clear message after 6 seconds
        setTimeout(() => {
          setValidationMessage('');
        }, 6000);
      }
    } catch (error) {
      console.error('Error validating cart items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced addToCart function with better debugging, special pricing handling and item consolidation
  const addToCart = (product) => {
    console.log('=== AddToCart Debug ===');
    console.log('Product being added:', product);
    console.log('Current cart items:', cartItems);
    
    // Check if product is an ID or an object
    let productToAdd;
    
    if (typeof product === 'object') {
      // Product is already an object, use it directly
      productToAdd = {
        ...product,
        unit: product.unit || 'each', // Ensure unit is always present
        // Preserve special pricing information from landing page
        effectiveUnitPrice: product.effectiveUnitPrice || product.price,
        originalPrice: product.originalPrice || product.price,
        hasSpecialPricing: product.hasSpecialPricing || false,
        specialPricingInfo: product.specialPricingInfo || null
      };
    } else {
      // Product is an ID, find the product in our sample list
      productToAdd = products.find(p => p.id === product);
      if (!productToAdd) {
        console.error('Product not found:', product);
        return;
      }
      productToAdd = {
        ...productToAdd,
        unit: productToAdd.unit || 'each', // Ensure unit is always present
        originalPrice: productToAdd.price
      };
    }

    console.log('Processed product to add:', productToAdd);

    // Enhanced item matching - check by ID and name for special-priced products
    const existingItemIndex = cartItems.findIndex(item => {
      // Primary match by ID
      if (item.id === productToAdd.id) {
        return true;
      }
      
      // Secondary match for special-priced products by name (in case IDs differ)
      if (
        item.name
        && productToAdd.name
        && (
          (isCornProduct(item) && isCornProduct(productToAdd))
          || (isEggProduct(item) && isEggProduct(productToAdd))
        )
      ) {
        console.log('Found potential special-priced match by name:', item.name, 'vs', productToAdd.name);
        return item.name.toLowerCase().trim() === productToAdd.name.toLowerCase().trim();
      }
      
      return false;
    });
    
    console.log('Existing item index:', existingItemIndex);
    
    let newCartItems;
    
    if (existingItemIndex !== -1) {
      // Item exists - combine quantities and recalculate any active special pricing
      const existingItem = cartItems[existingItemIndex];
      console.log('Combining with existing item:', existingItem);
      
      const newTotalQuantity = existingItem.quantity + (productToAdd.quantity || 1);
      console.log('New total quantity:', newTotalQuantity);

      const updatedItem = applySpecialPricingToItem({
        ...existingItem,
        ...productToAdd,
        id: existingItem.id,
        originalPrice: existingItem.originalPrice || productToAdd.originalPrice || existingItem.price || productToAdd.price,
        unit: existingItem.unit || productToAdd.unit || 'each'
      }, newTotalQuantity);
      console.log('Updated item:', updatedItem);

      newCartItems = [
        ...cartItems.slice(0, existingItemIndex),
        updatedItem,
        ...cartItems.slice(existingItemIndex + 1)
      ];
    } else {
      // Item doesn't exist, add it as new item
      console.log('Adding as new item');
      
      const newItem = applySpecialPricingToItem({
        ...productToAdd,
        quantity: productToAdd.quantity || 1
      }, productToAdd.quantity || 1);
      
      console.log('New item to add:', newItem);
      newCartItems = [...cartItems, newItem];
    }
    
    console.log('Final cart items:', newCartItems);
    console.log('=== End AddToCart Debug ===');
    setCartItems(newCartItems);
  };

  // Remove item from cart
  const removeFromCart = (productId) => {
    console.log('Removing from cart:', productId);
    const newCartItems = cartItems.filter(item => item.id !== productId);
    setCartItems(newCartItems);
  };

  // Update item quantity in cart with special pricing recalculation
  const updateQuantity = (productId, newQuantity) => {
    console.log('Updating quantity:', productId, newQuantity);
    
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }
    
    const newCartItems = cartItems.map(item => {
      if (item.id !== productId) {
        return item;
      }
      
      return applySpecialPricingToItem(item, newQuantity);
    });
    
    setCartItems(newCartItems);
  };

  // Clear cart
  const clearCart = () => {
    console.log('Clearing cart');
    setCartItems([]);
  };

  // Refresh cart validation
  const refreshCart = async () => {
    if (cartItems.length > 0) {
      await validateCartItems(cartItems);
    }
  };

  // Validate cart before checkout
  const validateCartForCheckout = () => {
    if (cartItems.length === 0) {
      return { valid: false, message: 'Your cart is empty' };
    }
    
    // Check if all items have valid prices
    const invalidItems = cartItems.filter(item => !item.price || item.price <= 0);
    if (invalidItems.length > 0) {
      return { valid: false, message: 'Some items have invalid prices' };
    }
    
    return { valid: true, message: '' };
  };

  // Calculate total items in cart
  const itemCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  // Calculate subtotal using effective unit prices for special pricing
  const subtotal = getCartSubtotal(cartItems);
  const originalSubtotal = getCartOriginalSubtotal(cartItems);
  const specialSavingsTotal = getCartSpecialSavings(cartItems);
  const eggBulkSavings = getCartEggBulkSavings(cartItems);
  const bulkSavingsLabel = getBulkSavingsLabel(cartItems);

  // Calculate total - delivery fee is now zone-based and determined at checkout
  const calculateTotal = () => {
    // Shipping is calculated at checkout based on delivery zone, so return subtotal only here
    return subtotal.toFixed(2);
  };

  // Get available products
  const getProducts = () => products;

  // Get product by id
  const getProduct = (productId) => products.find(p => p.id === productId);

  // Debug function
  const debugCart = () => {
    console.log('=== Cart Debug Info ===');
    console.log('Current cartItems state:', cartItems);
    console.log('localStorage bucksCart:', localStorage.getItem('bucksCart'));
    console.log('Parsed localStorage:', JSON.parse(localStorage.getItem('bucksCart') || '[]'));
    console.log('isCartLoaded:', isCartLoaded);
    console.log('isInitialMount:', isInitialMount.current);
    console.log('itemCount:', itemCount);
    console.log('subtotal:', subtotal);
    console.log('originalSubtotal:', originalSubtotal);
    console.log('specialSavingsTotal:', specialSavingsTotal);
  };

  // Additional debugging helper for manual cart cleanup
  const consolidateCartItems = () => {
    console.log('Consolidating cart items...');
    console.log('Before consolidation:', cartItems);
    
    const consolidatedItems = [];
    
    cartItems.forEach(item => {
      // Find existing item by name for corn products, or by ID for others
      const existingIndex = consolidatedItems.findIndex(existing => {
        if (item.name?.toLowerCase().includes('corn') && existing.name?.toLowerCase().includes('corn')) {
          return existing.name.toLowerCase().trim() === item.name.toLowerCase().trim();
        }
        return existing.id === item.id;
      });
      
      if (existingIndex !== -1) {
        // Combine quantities
        const existingItem = consolidatedItems[existingIndex];
        const newQuantity = existingItem.quantity + item.quantity;
        consolidatedItems[existingIndex] = applySpecialPricingToItem(existingItem, newQuantity);
      } else {
        consolidatedItems.push(applySpecialPricingToItem(item, item.quantity || 1));
      }
    });
    
    console.log('After consolidation:', consolidatedItems);
    setCartItems(consolidatedItems);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        itemCount,
        subtotal,
        originalSubtotal,
        specialSavingsTotal,
        eggBulkSavings,
        bulkSavingsLabel,
        isLoading,
        validationMessage,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        refreshCart,
        calculateTotal,
        getProducts,
        getProduct,
        validateCartForCheckout,
        debugCart,
        consolidateCartItems, // Add this new function
        isCartLoaded
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
